import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import Button from '../components/common/Button'
import ParrotMark from '../components/common/ParrotMark'
import Spinner from '../components/common/Spinner'
import PostCard from '../components/social/PostCard'
import UserPhoto from '../components/social/UserPhoto'
import { useAutoPost, usePublicProfilesEnabled } from '../hooks/useFeatures'
import { useFeedPlayer } from '../hooks/useFeedPlayer'
import { LEVEL_LABELS } from '../lib/home'
import { faDigits } from '../lib/numerals'
import { badgeLabel, postsNote, profileFailure, withMorePosts } from '../lib/publicProfile'
import { TONE, avatarById } from '../lib/shop'
import { refusalFor, removePost, replacePost, withClap, withComment, withFollow, withRating } from '../lib/social'
import { clapPost, commentPost, followUser, publicProfile, ratePost, unsharePost } from '../services/social'

function BackGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ltr:rotate-180" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  )
}

function StatTile({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5 card-soft rounded-tile px-2.5 py-3.5">
      <span className="tnum text-[19px] font-bold text-ink">{value}</span>
      <span className="text-center text-[10.5px] text-ink-2">{label}</span>
    </div>
  )
}

/**
 * The header, the three numbers and the badges of a profile answer — split
 * out so it renders from a test without a server. Only the fields the
 * contract's privacy table lists: display name, avatar/photo, level,
 * streak, month XP, practice count, badges. Never a phone, full name,
 * birthday or last-seen time.
 */
export function ProfileSummary({ profile, busy = false, onFollow }) {
  const { t } = useTranslation('publicProfile')
  const { author } = profile
  const tone = TONE[avatarById(author.avatar).tone] ?? TONE.brand
  const level = LEVEL_LABELS[author.level]
  const badges = Array.isArray(profile.badges) ? profile.badges : []

  return (
    <>
      <section className="flex items-center gap-[13px]" data-public-profile={author.id}>
        <UserPhoto photoUrl={author.photoUrl} size={64}>
          <span className={`grid size-16 shrink-0 place-items-center rounded-full border-[2.5px] ${tone.disc} ${tone.ring}`} aria-hidden="true">
            <ParrotMark size={36} />
          </span>
        </UserPhoto>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="truncate text-[19px] font-bold text-ink">
            {author.displayName}
            {author.me && <span className="ms-2 rounded-full bg-brand-50 px-2 py-0.5 align-middle text-[10.5px] font-semibold text-brand-700">{t('you')}</span>}
          </h1>
          {level && <p className="text-[12.5px] text-ink-2">{t('levelLine', { level })}</p>}
        </div>
        {!author.me && onFollow && (
          <button
            type="button"
            disabled={busy}
            aria-pressed={author.followedByMe === true}
            onClick={() => onFollow(author, !author.followedByMe)}
            className={`h-9 shrink-0 rounded-full px-3.5 text-[12px] font-bold transition-colors ${author.followedByMe ? 'bg-surface-2 text-ink-2' : 'bg-brand-600 text-white dark:text-slate-900'}`}
          >
            {author.followedByMe ? t('following') : t('follow')}
          </button>
        )}
      </section>

      <div className="grid grid-cols-3 gap-2">
        <StatTile value={faDigits(author.streakDays ?? 0)} label={t('stats.streak')} />
        <StatTile value={faDigits(profile.periodXp ?? 0)} label={t('stats.periodXp')} />
        <StatTile value={faDigits(profile.practiceCount ?? 0)} label={t('stats.practiceCount')} />
      </div>

      {badges.length > 0 && (
        <section className="space-y-2.5 card-soft p-3.5" data-badges={badges.length}>
          <h2 className="text-[13.5px] font-bold text-ink">{t('badgesHeading')}</h2>
          <ul className="flex flex-wrap gap-1.5">
            {badges.map((badge) => (
              <li key={`${badge.key}:${badge.periodKey}`} className="rounded-full bg-accent-tint px-2.5 py-1 text-[11.5px] font-bold text-accent-ink">
                🏅 {badgeLabel(badge)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

/**
 * A learner's public profile (V5-w, `/u/:userId`), opened by tapping a
 * name or an avatar in the feed, the leaderboard or the team. Everything is
 * `social_profile`'s: the author fields the feed already shows, the month's
 * XP, the practice count, badges, and their shared takes under the feed's
 * own privacy rule — while the public feed is off, someone else's profile
 * has no posts and no recording, and the page says so plainly.
 *
 * While public profiles are switched off (`me_profile.features.
 * publicProfilesEnabled`, V5-w-flags; production until stage 3) the page
 * asks the server nothing and shows a neutral "not available" state — a
 * link shared or typed by hand lands here, since the app itself links no
 * name then.
 */
export default function PublicProfile() {
  const { userId } = useParams()
  const { t } = useTranslation('publicProfile')
  // R13: while the server posts every scored take, there is no unsharing (the feed clears daily).
  const autoPost = useAutoPost()
  const navigate = useNavigate()
  const location = useLocation()
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const player = useFeedPlayer()
  const enabled = usePublicProfilesEnabled()

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    setError(null)
    setProfile(null)
    publicProfile(userId)
      .then((data) => {
        if (!cancelled) setProfile(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [userId, reload, enabled])

  const { error: playerError, clearError } = player
  useEffect(() => {
    if (!playerError) return
    setNotice(t('playerErrors.failed', { ns: 'social' }))
    clearError()
  }, [playerError, clearError, t])

  async function request(kind, call, apply) {
    if (busy) return
    setBusy(true)
    setNotice(null)
    try {
      apply(await call())
    } catch (e) {
      setNotice(refusalFor(e, kind))
    } finally {
      setBusy(false)
    }
  }

  const update = (post, next) => setProfile((current) => (current ? { ...current, posts: replacePost(current.posts, next(post)) } : current))

  const onClap = (post, on) => request('clap', () => clapPost(post.id, on), (result) => update(post, (p) => withClap(p, result)))
  const onRate = (post, stars) => request('rate', () => ratePost(post.id, stars), (result) => update(post, (p) => withRating(p, result)))
  const onComment = (post, text) => request('comment', () => commentPost(post.id, text), (comment) => update(post, (p) => withComment(p, comment)))
  const onFollow = (author, on) =>
    request('follow', () => followUser(author.id, on), (result) =>
      setProfile((current) =>
        current
          ? {
              ...current,
              author: current.author.id === author.id ? { ...current.author, followedByMe: result.following } : current.author,
              posts: withFollow(current.posts, author.id, result.following),
            }
          : current,
      ),
    )
  const onUnshare = (post) =>
    request('clap', () => unsharePost(post.id), (result) => {
      if (result.removed) {
        setProfile((current) => (current ? { ...current, posts: removePost(current.posts, post.id), postCount: Math.max(0, (current.postCount ?? 1) - 1) } : current))
        setNotice(t('removedNotice'))
      }
    })

  async function loadMore() {
    if (!profile?.nextCursor || busy) return
    setBusy(true)
    try {
      const next = await publicProfile(userId, { cursor: profile.nextCursor })
      setProfile((current) => withMorePosts(current, next))
    } catch (e) {
      setNotice(refusalFor(e, 'clap'))
    } finally {
      setBusy(false)
    }
  }

  const failure = error && !profile ? profileFailure(error) : null
  const note = postsNote(profile)

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <button type="button" onClick={() => (location.key === 'default' ? navigate('/social') : navigate(-1))} aria-label={t('back')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <BackGlyph />
        </button>
        <p className="flex-1 text-lg font-bold text-ink">{t('title')}</p>
      </header>

      {notice && (
        <p role="status" className="rounded-xl bg-bad-tint px-3.5 py-2.5 text-xs font-semibold text-bad-ink">
          {notice}
        </p>
      )}

      {!enabled ? (
        <div className="flex flex-col items-center gap-3 px-2.5 py-8 text-center" data-profile-state="unavailable">
          <span className="grid size-[84px] place-items-center rounded-full bg-surface-2" aria-hidden="true">
            <ParrotMark size={40} />
          </span>
          <p className="text-[17px] font-bold text-ink">{t('unavailable.title')}</p>
          <p className="max-w-[280px] text-[12.5px] leading-[1.9] text-ink-2">{t('unavailable.body')}</p>
        </div>
      ) : failure === 'not_found' ? (
        <div className="flex flex-col items-center gap-3 px-2.5 py-8 text-center" data-profile-state="not-found">
          <span className="grid size-[84px] place-items-center rounded-full bg-surface-2" aria-hidden="true">
            <ParrotMark size={40} />
          </span>
          <p className="text-[17px] font-bold text-ink">{t('notFound.title')}</p>
          <p className="max-w-[280px] text-[12.5px] leading-[1.9] text-ink-2">{t('notFound.body')}</p>
        </div>
      ) : failure ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${failure === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {failure === 'offline' ? t('error.offline') : t('error.other')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('retry')}
          </Button>
        </div>
      ) : !profile ? (
        <Spinner label={t('loading')} />
      ) : (
        <>
          <ProfileSummary profile={profile} busy={busy} onFollow={onFollow} />

          <section className="space-y-3">
            <h2 className="flex items-center gap-2 text-[15px] font-bold text-ink">
              {t('postsHeading')}
              {profile.postCount > 0 && <span className="tnum rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-semibold text-ink-2">{faDigits(profile.postCount)}</span>}
            </h2>
            {note ? (
              <p className="card-soft p-4 text-[12.5px] leading-[1.9] text-ink-2" data-posts-note={note}>
                {t(`posts.${note}`)}
              </p>
            ) : (
              <>
                {profile.posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    player={player}
                    busy={busy}
                    onClap={onClap}
                    onRate={onRate}
                    onComment={onComment}
                    onFollow={onFollow}
                    onUnshare={autoPost ? undefined : onUnshare}
                  />
                ))}
                {profile.nextCursor && (
                  <Button variant="secondary" className="w-full" loading={busy} onClick={loadMore}>
                    {t('loadMore')}
                  </Button>
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}
